
import { initializeApp, FirebaseApp } from "firebase/app";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  initializeFirestore,
  persistentLocalCache,
  memoryLocalCache,
  query,
  where,
  or,
  onSnapshot,
  Firestore,
  DocumentData,
  QuerySnapshot,
  DocumentSnapshot,
  WithFieldValue,
  UpdateData,
  connectFirestoreEmulator
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  FirebaseStorage,
  connectStorageEmulator
} from "firebase/storage";
import {
  getFunctions,
  Functions,
  connectFunctionsEmulator
} from "firebase/functions";
import {
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  Auth
} from "firebase/auth";
import { firebaseConfig } from '../firebaseConfig';

/**
 * E2E-Tests gegen die Firebase-Emulatoren: nur im Build `vite build --mode e2e`. Im normalen Build ist
 * die Bedingung eine Konstante (false), der Emulator-Code entfällt; die Pipeline prüft das.
 */
export const USE_EMULATORS = import.meta.env.MODE === 'e2e';

// Firebase initialisieren
const app: FirebaseApp = initializeApp(firebaseConfig);

// Performance Monitoring has been disabled to prevent Safari from detecting it as a "Tracker"
// which violates the privacy policy.
// import { getPerformance } from "firebase/performance";
// 
// if (typeof window !== 'undefined') {
//   getPerformance(app);
// }

import { initializeAppCheck, getToken as getAppCheckToken, AppCheck } from "firebase/app-check";
import { turnstileAppCheckProviderInstance } from "./TurnstileAppCheckProvider";

let appCheck: AppCheck | null = null;

try {
  // Die Emulatoren prüfen App Check nicht; ohne Turnstile bleibt der E2E-Test frei von externen Diensten
  if (typeof window !== 'undefined' && !USE_EMULATORS) {
    // Note: You must register your site in Firebase Console > App Check > Apps 
    // and get a Recaptcha Enterprise Site Key.
    // Add VITE_RECAPTCHA_SITE_KEY to your .env file.
    const _appCheckKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || 'PLACEHOLDER_KEY_PLEASE_CONFIGURE';

    // IMMER initialisieren, um Security Policy enforced zu halten.
    // Wenn in Safari ITP blockiert, lehnt Firebase SDK durch die Provider-Fehler intern "sicher" ab.
    if (location.hostname === 'localhost') {
      // Localhost: Generates a debug token in console for use in Firebase Console
      // @ts-expect-error - self.FIREBASE_APPCHECK_DEBUG_TOKEN is needed for debug mode
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true;
      appCheck = initializeAppCheck(app, {
        provider: turnstileAppCheckProviderInstance,
        isTokenAutoRefreshEnabled: true
      });
      console.log('App Check Debug Mode enabled. Check console for Token.');
    } else {
      // Prod / Test / Staging Environments
      appCheck = initializeAppCheck(app, {
        provider: turnstileAppCheckProviderInstance,
        isTokenAutoRefreshEnabled: true
      });
      console.log('App Check initialized (Custom Turnstile).');
    }
  }
} catch (e) {
  console.warn("App Check failed to load:", e);
}

/** App Check hat vor einem Aufruf kein gültiges Token geliefert (Turnstile noch nicht gelöst oder Fehler). */
export class AppCheckUnavailableError extends Error {
  constructor(cause: unknown) {
    super('App Check token unavailable', { cause });
    this.name = 'AppCheckUnavailableError';
  }
}

/**
 * Wartet auf ein gültiges App-Check-Token, z.B. vor einem Aufruf auf der Login-Seite. Ohne Token sendet das
 * Functions-SDK den Aufruf ohne App Check, und Functions mit erzwungenem App Check lehnen ihn ab. Jeder Versuch
 * wartet im TurnstileAppCheckProvider bis zu 4 s auf Turnstile; dessen Prüfung läuft dazwischen weiter.
 * Ohne App Check (Emulatoren) sofort erledigt.
 */
export const waitForAppCheckToken = async (attempts = 2): Promise<void> => {
  if (!appCheck) return;
  for (let attempt = 1; ; attempt++) {
    try {
      await getAppCheckToken(appCheck);
      return;
    } catch (e) {
      if (attempt >= attempts) throw new AppCheckUnavailableError(e);
    }
  }
};

// Auth
export const auth: Auth = getAuth(app);
auth.languageCode = 'de'; // Standardsprache auf Deutsch setzen

// Firestore - Safe Initialization Wrapper
const initFirestore = (firebaseApp: FirebaseApp): Firestore => {
  try {
    // Detect Safari (excluding Chrome and Android browsers)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    return initializeFirestore(firebaseApp, {
      localCache: isSafari ? memoryLocalCache() : persistentLocalCache({}),
      experimentalForceLongPolling: isSafari
    });
  } catch (e: unknown) {
    const error = e as { code?: string; message?: string };
    // Fallback: If Firestore has already been initialized (e.g. by Fast Refresh), use the existing instance.
    // This prevents the "Firestore has already been started" crash during development.
    if (error.code === 'failed-precondition' || (error.message && error.message.includes('already been started'))) {
      return getFirestore(firebaseApp);
    }
    console.error("Firestore initialization failed:", e);
    throw e;
  }
};

export const db: Firestore = initFirestore(app);

// Storage
export const firebaseStorage: FirebaseStorage = getStorage(app);

if (USE_EMULATORS) {
  console.info('[E2E] Emulator-Modus: Auth, Firestore, Functions und Storage laufen lokal');
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(firebaseStorage, '127.0.0.1', 9199);
}

// Functions
// IMPORTANT: We specify that the frontend should look for functions in 'europe-west6' (Zurich)
let firebaseFunctionsInstance: Functions | null = null;
try {
  firebaseFunctionsInstance = getFunctions(app, 'europe-west6');
  if (USE_EMULATORS) connectFunctionsEmulator(firebaseFunctionsInstance, '127.0.0.1', 5001);
} catch (e) {
  console.warn("Firebase Functions initialization failed (Service 'functions' is not available). Check importmap.", e);
}
export const firebaseFunctions = firebaseFunctionsInstance;

// Export a promise for persistence compatibility with index.tsx
export const persistencePromise = Promise.resolve();

// Helper to build tenant-aware path
export const getTenantCollectionPath = (tenantId: string, collectionName: string) => {
  return `tenants/${tenantId}/${collectionName}`;
};

// --- Storage Functions ---
export const uploadFile = async (file: File, path: string): Promise<string> => {
  try {
    const storageRef = ref(firebaseStorage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

// --- Generische Firestore-Funktionen ---

export const getCollection = async <T>(path: string): Promise<(T & { id: string })[]> => {
  const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(collection(db, path));
  return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as T & { id: string }));
};

export const getDocument = async <T>(collectionPath: string, docId: string): Promise<(T & { id: string }) | null> => {
  const docRef = doc(db, collectionPath, docId);
  const docSnap: DocumentSnapshot<DocumentData> = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as T & { id: string };
  } else {
    return null;
  }
};

/**
 * Hört auf ein Dokument. Anders als getDocument bricht das nicht mit «client is offline» ab, wenn Firestore beim
 * Laden noch keine Verbindung hat: Der erste Wert kommt dann aus dem Cache (oder fehlt), der echte folgt.
 */
export const subscribeDocument = <T>(
  collectionPath: string,
  docId: string,
  onData: (data: (T & { id: string }) | null) => void,
  onError: (error: Error) => void
): (() => void) => onSnapshot(
  doc(db, collectionPath, docId),
  (snap) => onData(snap.exists() ? ({ id: snap.id, ...snap.data() } as T & { id: string }) : null),
  onError
);

export const addDocument = async <T extends DocumentData>(collectionPath: string, data: WithFieldValue<T>): Promise<string> => {
  const docRef = await addDoc(collection(db, collectionPath), data);
  return docRef.id;
}

export const setDocument = async <T extends DocumentData>(collectionPath: string, docId: string, data: WithFieldValue<T>): Promise<void> => {
  await setDoc(doc(db, collectionPath, docId), data);
}

export const updateDocument = async <T extends DocumentData>(collectionPath: string, docId: string, data: UpdateData<T>): Promise<void> => {
  await updateDoc(doc(db, collectionPath, docId), data);
}

export const deleteDocument = async (collectionPath: string, docId: string): Promise<void> => {
  await deleteDoc(doc(db, collectionPath, docId));
}

// Export Auth & Firestore & Functions helpers
export {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  collection,
  query,
  where,
  or,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  confirmPasswordReset,
  verifyPasswordResetCode
};

// --- Custom httpsCallable Wrapper for Cloud Armor Support ---
import { httpsCallableFromURL, httpsCallable as firebaseHttpsCallable, HttpsCallable, HttpsCallableOptions } from "firebase/functions";

/**
 * The SDK appends the HTTP status to the backend message, e.g. "backend.errors.kiAbrechnung [400]".
 * The app translates and displays these messages, so they must reach it as the backend sent them.
 */
const withBackendMessage = (callable: HttpsCallable): HttpsCallable => {
  const call = async (data?: unknown) => {
    try {
      return await callable(data);
    } catch (error) {
      if (error instanceof Error) error.message = error.message.replace(/ \[\d+\]$/, '');
      throw error;
    }
  };
  return Object.assign(call, { stream: callable.stream });
};

/**
 * Wrapper for httpsCallable that switches between:
 * 1. Standard Cloud Functions (for localhost/dev)
 * 2. Cloud Armor Load Balancer (api.caf-suite.ch) for Production
 */
export const httpsCallable = (functionsInstance: Functions | null, name: string, options?: HttpsCallableOptions) => {
  // CONFIGURATION: Set this to true to force using the Load Balancer
  // Uses environment variable, default is false (safer for Dev/Test)
  const USE_LOAD_BALANCER = import.meta.env.VITE_USE_LOAD_BALANCER !== 'false';
  const API_DOMAIN = import.meta.env.VITE_API_DOMAIN || 'https://api.caf-suite.ch';

  if (USE_LOAD_BALANCER && functionsInstance) {
    // Route through Cloud Armor Load Balancer
    // URL Pattern: https://api.caf-suite.ch/<functionName>
    // The LB uses a URL Mask to route automatically to the correct Cloud Run service.
    // Cloud Run services are always lowercase, so we ensure the path reflects that.
    const customUrl = `${API_DOMAIN}/${name.toLowerCase()}`;
    console.debug(`[Security] Routing function '${name}' via Cloud Armor: ${customUrl}`);
    return withBackendMessage(httpsCallableFromURL(functionsInstance, customUrl, options));
  } else {
    // Fallback to standard internal routing (direct to Cloud Run or Emulator)
    // This is needed for localhost testing or if LB is not ready
    console.debug(`[Security] Routing function '${name}' directly to Cloud Functions/Emulator.`);
    if (!functionsInstance) throw new Error("Firebase Functions not initialized");
    return withBackendMessage(firebaseHttpsCallable(functionsInstance, name, options));
  }
};
